import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hash, ChatCircleDots, Plus, MagnifyingGlass, PushPin, Lock, Trash, X, UsersThree, CaretLeft, Robot, Sparkle } from '@phosphor-icons/react';
import { useAuth } from '@/auth/AuthContext';
import { Input } from '@/components/ui/input';
import Composer from '@/components/chat/Composer';
import MessageList from '@/components/chat/MessageList';
import NewChannelDialog from '@/components/chat/NewChannelDialog';
import MembersDialog from '@/components/chat/MembersDialog';
import AiImportPanel from '@/components/chat/AiImportPanel';
import AssistantPanel from '@/components/chat/AssistantPanel';
import AssistantConvList from '@/components/chat/AssistantConvList';

const CaretIndicator = ({ open }) => (
  <span className={`text-[10px] text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
);

const AI_IMPORT_ID = '__ai_import__';
const ASSISTANT_ID = '__assistant__';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sortChannels(list) {
  return [...list].sort((a, b) => {
    if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
    const at = a.last_message?.created_at || a.updated_at || '';
    const bt = b.last_message?.created_at || b.updated_at || '';
    return bt.localeCompare(at);
  });
}

export default function Chat() {
  const { user } = useAuth();
  const [directory, setDirectory] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [typingNames, setTypingNames] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [askAiMsg, setAskAiMsg] = useState(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  const active = channels.find((c) => c.id === activeId) || null;
  const isAiImport = activeId === AI_IMPORT_ID;
  const isAssistant = activeId === ASSISTANT_ID;
  const canImport = user?.role === 'admin' || user?.role === 'manager';

  const loadChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/channels`);
      setChannels(sortChannels(res.data || []));
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/chat/directory`).then((r) => setDirectory(r.data || [])).catch(() => {});
    loadChannels();
  }, [loadChannels]);

  // Auto-select first channel on desktop only (mobile shows the list first)
  useEffect(() => {
    if (!activeId && channels.length && window.innerWidth >= 768) setActiveId(channels[0].id);
  }, [channels, activeId]);

  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const res = await axios.get(`${API}/chat/channels/${cid}/messages`);
      if (activeRef.current === cid) setMessages(res.data || []);
      axios.post(`${API}/chat/channels/${cid}/read`).then(loadChannels).catch(() => {});
    } catch (_e) { /* ignore */ }
  }, [loadChannels]);

  // Load + poll messages for the active channel
  useEffect(() => {
    if (!activeId || activeId === AI_IMPORT_ID || activeId === ASSISTANT_ID) { setMessages([]); return undefined; }
    setReplyTo(null);
    loadMessages(activeId);
    const id = setInterval(() => loadMessages(activeId), 2500);
    return () => clearInterval(id);
  }, [activeId, loadMessages]);

  // Poll typing indicators for the active channel
  useEffect(() => {
    if (!activeId || activeId === AI_IMPORT_ID || activeId === ASSISTANT_ID) { setTypingNames([]); return undefined; }
    const tick = () => axios.get(`${API}/chat/channels/${activeId}/typing`)
      .then((r) => { if (activeRef.current === activeId) setTypingNames(r.data || []); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 2500);
    return () => clearInterval(id);
  }, [activeId]);

  const pingTyping = useCallback(() => {
    if (activeId) axios.post(`${API}/chat/channels/${activeId}/typing`).catch(() => {});
  }, [activeId]);

  const sendMessage = async ({ text, attachments, mentions }) => {
    await axios.post(`${API}/chat/channels/${activeId}/messages`, {
      text, attachments, mentions, reply_to: replyTo?.id || null,
    });
    setReplyTo(null);
    await loadMessages(activeId);
  };

  const editMessage = async (mid, text) => {
    await axios.put(`${API}/chat/messages/${mid}`, { text });
    await loadMessages(activeId);
  };

  const react = async (mid, emoji) => {
    try { await axios.post(`${API}/chat/messages/${mid}/react`, { emoji }); await loadMessages(activeId); }
    catch (_e) { toast.error('リアクションに失敗しました'); }
  };
  const pin = async (mid) => {
    try { await axios.post(`${API}/chat/messages/${mid}/pin`); await loadMessages(activeId); }
    catch (_e) { toast.error('ピン留めに失敗しました'); }
  };
  const del = async (mid) => {
    if (!window.confirm('このメッセージを削除しますか？')) return;
    try { await axios.delete(`${API}/chat/messages/${mid}`); await loadMessages(activeId); }
    catch (_e) { toast.error('削除に失敗しました'); }
  };

  const startDm = async (userId) => {
    try {
      const res = await axios.post(`${API}/chat/dm`, { user_id: userId });
      await loadChannels();
      setActiveId(res.data.id);
    } catch (_e) { toast.error('DMの作成に失敗しました'); }
  };

  const togglePinChannel = async () => {
    if (!active) return;
    try { await axios.put(`${API}/chat/channels/${active.id}`, { pinned: !active.pinned }); await loadChannels(); }
    catch (_e) { toast.error('更新に失敗しました'); }
  };

  const deleteChannel = async () => {
    if (!active) return;
    const isDm = active.type === 'dm';
    const msg = isDm
      ? `${active.display_name} とのダイレクトメッセージを削除しますか？\n（両方の履歴が削除されます）`
      : `チャンネル「${active.display_name}」を削除しますか？`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/chat/channels/${active.id}`);
      setActiveId(null);
      await loadChannels();
      toast.success(isDm ? 'ダイレクトメッセージを削除しました' : 'チャンネルを削除しました');
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    }
  };

  const runSearch = async (term) => {
    setSearch(term);
    if (!term.trim()) { setSearchResults(null); return; }
    try {
      const res = await axios.get(`${API}/chat/search`, { params: { q: term.trim() } });
      setSearchResults(res.data || []);
    } catch (_e) { /* ignore */ }
  };

  const channelList = channels.filter((c) => c.type === 'channel');
  const dmList = channels.filter((c) => c.type === 'dm');

  const ChannelRow = ({ c }) => (
    <button type="button" onClick={() => { setActiveId(c.id); setSearch(''); setSearchResults(null); }}
      data-testid={`channel-row-${c.id}`}
      className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
        activeId === c.id ? 'bg-teal-700/10 text-teal-700' : 'text-zinc-700 hover:bg-zinc-100'
      }`}>
      {c.type === 'dm'
        ? <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold flex items-center justify-center shrink-0">{(c.display_name || '?').charAt(0).toUpperCase()}</span>
        : (c.is_private ? <Lock size={15} className="shrink-0" /> : <Hash size={15} className="shrink-0" />)}
      <span className="flex-1 truncate text-sm font-medium">{c.display_name}</span>
      {c.pinned && <PushPin size={11} weight="fill" className="text-amber-500 shrink-0" />}
      {c.unread > 0 && (
        <span data-testid={`channel-unread-${c.id}`} className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>
      )}
    </button>
  );

  return (
    <div className="flex flex-col page-enter" style={{ height: 'calc(100vh - 160px)' }}>
      <div className="mb-3">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">チャット</h1>
        <p className="text-xs sm:text-sm text-zinc-500">チームでリアルタイムに会話できます。</p>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3">
        {/* Sidebar — hidden on mobile while a conversation is open */}
        <aside className={`bg-white border border-zinc-200 rounded-xl flex-col min-h-0 ${activeId || searchResults !== null ? 'hidden md:flex' : 'flex'}`} data-testid="chat-sidebar">
          <div className="p-3 border-b border-zinc-200 space-y-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={search} onChange={(e) => runSearch(e.target.value)} placeholder="メッセージを検索..." className="pl-9 h-9" data-testid="chat-search-input" />
            </div>
            <button type="button" onClick={() => setNewOpen(true)} data-testid="chat-new-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-teal-700 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
              <Plus size={16} weight="bold" /> 新しい会話
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            <p className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">AIアシスタント</p>
            <button type="button"
              onClick={() => { setActiveId(ASSISTANT_ID); setSearch(''); setSearchResults(null); }}
              data-testid="assistant-nav-btn"
              className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors mb-1 ${
                isAssistant ? 'bg-violet-100 text-violet-700' : 'text-zinc-700 hover:bg-zinc-100'
              }`}>
              <span className="w-6 h-6 rounded-md bg-teal-700 text-white flex items-center justify-center shrink-0">
                <Sparkle size={14} weight="fill" />
              </span>
              <span className="flex-1 truncate text-sm font-semibold">Sensbey AIアシスタント</span>
              <CaretIndicator open={isAssistant} />
            </button>
            {isAssistant && (
              <AssistantConvList onSelectAssistant={() => { setActiveId(ASSISTANT_ID); setSearch(''); setSearchResults(null); }} />
            )}
            {canImport && (
              <button type="button"
                onClick={() => { setActiveId(AI_IMPORT_ID); setSearch(''); setSearchResults(null); }}
                data-testid="ai-import-nav-btn"
                className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors mb-1 ${
                  isAiImport ? 'bg-violet-100 text-violet-700' : 'text-zinc-700 hover:bg-zinc-100'
                }`}>
                <span className="w-6 h-6 rounded-md bg-teal-700 text-white flex items-center justify-center shrink-0">
                  <Robot size={14} weight="fill" />
                </span>
                <span className="flex-1 truncate text-sm font-semibold">AI データ取り込み</span>
                <span className="text-[9px] font-bold text-violet-600 bg-violet-100 rounded px-1.5 py-0.5 shrink-0">AI</span>
              </button>
            )}
            <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">チャンネル</p>
            {channelList.length === 0 && <p className="px-3 py-1 text-xs text-zinc-400">まだありません</p>}
            {channelList.map((c) => <ChannelRow key={c.id} c={c} />)}
            <p className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">ダイレクトメッセージ</p>
            {dmList.length === 0 && <p className="px-3 py-1 text-xs text-zinc-400">まだありません</p>}
            {dmList.map((c) => <ChannelRow key={c.id} c={c} />)}
          </div>
        </aside>

        {/* Conversation — hidden on mobile until a channel/search is active */}
        <section className={`bg-white border border-zinc-200 rounded-xl flex-col min-h-0 ${activeId || searchResults !== null ? 'flex' : 'hidden md:flex'}`} data-testid="chat-conversation">
          {isAssistant ? (
            <AssistantPanel
              onBack={() => setActiveId(null)}
              attachedMessage={askAiMsg}
              onClearAttached={() => setAskAiMsg(null)}
            />
          ) : isAiImport ? (
            <AiImportPanel onBack={() => setActiveId(null)} />
          ) : searchResults !== null ? (
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-zinc-900">検索結果（{searchResults.length}）</span>
                <button type="button" onClick={() => { setSearch(''); setSearchResults(null); }} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
              </div>
              {searchResults.length === 0 ? (
                <p className="p-8 text-center text-sm text-zinc-400">該当するメッセージがありません</p>
              ) : searchResults.map((m) => (
                <button key={m.id} type="button" onClick={() => { setActiveId(m.channel_id); setSearch(''); setSearchResults(null); }}
                  data-testid={`search-result-${m.id}`}
                  className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-50">
                  <p className="text-xs text-zinc-400 mb-0.5"><span data-no-translate>{m.user_name}</span></p>
                  <p className="text-sm text-zinc-800 truncate">{m.text}</p>
                </button>
              ))}
            </div>
          ) : active ? (
            <>
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2" data-testid="chat-conversation-header">
                <button type="button" onClick={() => setActiveId(null)} data-testid="chat-back-btn"
                  className="md:hidden p-1.5 -ml-1.5 rounded text-zinc-500 hover:bg-zinc-100 shrink-0" title="一覧へ戻る">
                  <CaretLeft size={18} weight="bold" />
                </button>
                {active.type === 'dm'
                  ? <ChatCircleDots size={18} className="text-zinc-500 shrink-0" />
                  : (active.is_private ? <Lock size={18} className="text-zinc-500 shrink-0" /> : <Hash size={18} className="text-zinc-500 shrink-0" />)}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-zinc-900 truncate">{active.display_name}</p>
                  {active.description && <p className="text-[11px] text-zinc-400 truncate">{active.description}</p>}
                </div>
                <div className="flex items-center gap-1 ml-1">
                  {(active.tags || []).map((t) => (
                    <span key={t} className="text-[10px] bg-zinc-100 text-zinc-500 rounded-full px-2 py-0.5">#{t}</span>
                  ))}
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {active.type === 'channel' && (
                    <button type="button" onClick={() => setMembersOpen(true)} data-testid="channel-members-btn"
                      className="p-1.5 rounded text-zinc-400 hover:text-teal-700 hover:bg-zinc-100" title="メンバー管理">
                      <UsersThree size={16} />
                    </button>
                  )}
                  <button type="button" onClick={togglePinChannel} data-testid="channel-pin-toggle"
                    className="p-1.5 rounded text-zinc-400 hover:text-amber-600 hover:bg-zinc-100" title="チャンネルをピン留め">
                    <PushPin size={16} weight={active.pinned ? 'fill' : 'regular'} />
                  </button>
                  {active.type === 'channel' && (active.created_by === user?.id || user?.role === 'admin') && (
                    <button type="button" onClick={deleteChannel} data-testid="channel-delete-btn"
                      className="p-1.5 rounded text-zinc-400 hover:text-red-600 hover:bg-zinc-100" title="チャンネルを削除">
                      <Trash size={16} />
                    </button>
                  )}
                  {active.type === 'dm' && (
                    <button type="button" onClick={deleteChannel} data-testid="dm-delete-btn"
                      className="p-1.5 rounded text-zinc-400 hover:text-red-600 hover:bg-zinc-100" title="ダイレクトメッセージを削除">
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </div>
              <MessageList messages={messages} currentUser={user} onReact={react} onPin={pin} onDelete={del} onReply={setReplyTo} onEdit={editMessage}
                onAskAi={(m) => { setAskAiMsg(m); setActiveId(ASSISTANT_ID); setSearch(''); setSearchResults(null); }} />
              {typingNames.length > 0 && (
                <div className="px-4 py-1 text-[11px] text-zinc-400 italic" data-testid="typing-indicator">
                  {typingNames.slice(0, 3).join('、')} さんが入力中…
                </div>
              )}
              {active.id === 'chan-announcements' && user?.role !== 'admin' ? (
                <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 text-center" data-testid="announcements-readonly-notice">
                  <p className="text-xs text-zinc-500">アナウンス専用チャンネルです（閲覧のみ）</p>
                </div>
              ) : (
                <Composer directory={directory} channelId={activeId} onSend={sendMessage} onTyping={pingTyping} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} />
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <ChatCircleDots size={48} className="text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-500">会話を選択するか、新しい会話を始めましょう。</p>
              <button type="button" onClick={() => setNewOpen(true)} className="mt-3 text-sm text-teal-700 font-medium hover:underline" data-testid="chat-empty-new-btn">
                + 新しい会話を作成
              </button>
            </div>
          )}
        </section>
      </div>

      <NewChannelDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        directory={directory}
        currentUser={user}
        onCreated={(ch) => { loadChannels(); setActiveId(ch.id); }}
        onStartDm={startDm}
      />
      <MembersDialog
        open={membersOpen}
        onOpenChange={setMembersOpen}
        channel={active}
        directory={directory}
        currentUser={user}
        onChanged={loadChannels}
      />
    </div>
  );
}
